import React, { useMemo, useState } from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown, Search, Settings2 } from 'lucide-react';
import { cn } from './cn';
import { Input } from './Input';
import { Pagination } from './Pagination';
import { LoadingView } from './Spinner';
import { EmptyState } from './EmptyState';
import { Dropdown } from './Dropdown';
import { Checkbox } from './Checkbox';
import { foldVN, tokensOf } from './comboUtils';

/**
 * DataTable — bảng dùng chung (thay 74 bảng tay).
 *
 * columns: [{
 *   key, header, render?(row, idx), sortable?, sortAccessor?(row),
 *   className?, headerClassName?, align?: 'left'|'right'|'center', sticky?: boolean
 * }]
 * data: mảng object
 * keyField: tên field id (mặc định 'id')
 * Tính năng: searchable, sort client, phân trang client (pageSize), sticky cột đầu, ẩn/hiện cột.
 * sort/onSortChange: truyền cả hai để tự điều khiển sắp xếp (phía máy chủ) — {key, dir:'asc'|'desc'}.
 */
export function DataTable({
  columns = [],
  data = [],
  keyField = 'id',
  loading = false,
  searchable = false,
  searchPlaceholder = 'Tìm kiếm...',
  searchKeys,
  pageSize = 0, // 0 = không phân trang
  stickyFirst = false,
  columnToggle = false,
  emptyTitle = 'Không có dữ liệu',
  emptyDescription,
  emptyIcon,
  toolbar,
  onRowClick,
  rowClassName,
  className,
  tableClassName,
  sort: sortProp,
  onSortChange,
}) {
  const [query, setQuery] = useState('');
  const [localSort, setLocalSort] = useState({ key: null, dir: 'asc' });
  // Sắp xếp do bên ngoài điều khiển (phân trang phía máy chủ): bảng chỉ vẽ mũi tên và báo lại, KHÔNG tự xếp 50 dòng đang có
  // — xếp trong một trang thì "Bảo hành tăng dần" không phải hạn sớm nhất toàn danh sách.
  const controlled = sortProp !== undefined && typeof onSortChange === 'function';
  const sort = controlled ? sortProp : localSort;
  const [page, setPage] = useState(1);
  const [hidden, setHidden] = useState(() => new Set());

  const visibleColumns = useMemo(() => columns.filter((c) => !hidden.has(c.key)), [columns, hidden]);

  const getCellValue = (col, row) => {
    if (col.sortAccessor) return col.sortAccessor(row);
    return row[col.key];
  };

  // Lọc theo từ khoá
  const filtered = useMemo(() => {
    if (!searchable || !query.trim()) return data;
    // Tìm không dấu, nhiều từ không cần đúng thứ tự: "dong ho" khớp "Đồng hồ".
    const tokens = tokensOf(query);
    const keys = searchKeys || columns.map((c) => c.key);
    return data.filter((row) => {
      const hay = foldVN(keys.map((k) => (row[k] != null ? String(row[k]) : '')).join(' '));
      return tokens.every((t) => hay.includes(t));
    });
  }, [data, query, searchable, searchKeys, columns]);

  // Sắp xếp
  const sorted = useMemo(() => {
    if (controlled || !sort.key) return filtered;
    const col = columns.find((c) => c.key === sort.key);
    if (!col) return filtered;
    const arr = [...filtered];
    arr.sort((a, b) => {
      const va = getCellValue(col, a);
      const vb = getCellValue(col, b);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      let cmp;
      if (typeof va === 'number' && typeof vb === 'number') cmp = va - vb;
      else cmp = String(va).localeCompare(String(vb), 'vi', { numeric: true });
      return sort.dir === 'asc' ? cmp : -cmp;
    });
    return arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, sort, columns, controlled]);

  // Phân trang
  const pageCount = pageSize > 0 ? Math.max(1, Math.ceil(sorted.length / pageSize)) : 1;
  const safePage = Math.min(page, pageCount);
  const rows = pageSize > 0 ? sorted.slice((safePage - 1) * pageSize, safePage * pageSize) : sorted;

  const toggleSort = (col) => {
    if (!col.sortable) return;
    const next = sort.key === col.key
      ? { key: col.key, dir: sort.dir === 'asc' ? 'desc' : 'asc' }
      : { key: col.key, dir: 'asc' };
    if (controlled) onSortChange(next); else setLocalSort(next);
  };

  const toggleColumn = (key) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const alignClass = (a) => (a === 'right' ? 'text-right' : a === 'center' ? 'text-center' : 'text-left');

  return (
    <div className={cn('w-full', className)}>
      {(searchable || columnToggle || toolbar) && (
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2 flex-1 min-w-[200px] max-w-sm">
            {searchable ? (
              <Input
                leftIcon={Search}
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setPage(1);
                }}
                placeholder={searchPlaceholder}
                aria-label="Tìm kiếm trong bảng"
              />
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            {toolbar}
            {columnToggle ? (
              <Dropdown label="Cột" icon={Settings2} align="right" triggerClassName="btn-sm">
                <div className="px-2 py-1.5 space-y-1.5 max-h-64 overflow-y-auto">
                  {columns.map((c) => (
                    <Checkbox
                      key={c.key}
                      id={`col-${c.key}`}
                      label={typeof c.header === 'string' ? c.header : c.key}
                      checked={!hidden.has(c.key)}
                      onChange={() => toggleColumn(c.key)}
                    />
                  ))}
                </div>
              </Dropdown>
            ) : null}
          </div>
        </div>
      )}

      <div className="relative overflow-x-auto rounded-xl border border-line bg-surface shadow-xs">
        <table className={cn('w-full text-sm text-left', tableClassName)}>
          <thead className="bg-surface-sunken text-body border-b border-line">
            <tr>
              {visibleColumns.map((col, ci) => {
                const isSorted = sort.key === col.key;
                const sticky = stickyFirst && ci === 0;
                return (
                  <th
                    key={col.key}
                    scope="col"
                    className={cn(
                      'px-4 py-3 font-medium whitespace-nowrap',
                      alignClass(col.align),
                      col.sortable && 'cursor-pointer select-none hover:text-heading',
                      sticky && 'sticky left-0 z-10 bg-surface-sunken',
                      col.headerClassName
                    )}
                    onClick={() => toggleSort(col)}
                    onKeyDown={col.sortable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSort(col); } } : undefined}
                    tabIndex={col.sortable ? 0 : undefined}
                    aria-sort={isSorted ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                  >
                    <span className="inline-flex items-center gap-1.5">
                      {col.header}
                      {col.sortable ? (
                        isSorted ? (
                          sort.dir === 'asc' ? (
                            <ChevronUp className="w-3.5 h-3.5" />
                          ) : (
                            <ChevronDown className="w-3.5 h-3.5" />
                          )
                        ) : (
                          <ChevronsUpDown className="w-3.5 h-3.5 opacity-40" />
                        )
                      ) : null}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={visibleColumns.length}>
                  <LoadingView />
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={visibleColumns.length}>
                  <EmptyState title={emptyTitle} description={emptyDescription} icon={emptyIcon} />
                </td>
              </tr>
            ) : (
              rows.map((row, ri) => (
                <tr
                  key={row[keyField] ?? ri}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={cn(
                    'border-b border-line last:border-0 transition-colors',
                    'hover:bg-surface-sunken/60',
                    onRowClick && 'cursor-pointer',
                    typeof rowClassName === 'function' ? rowClassName(row) : rowClassName
                  )}
                >
                  {visibleColumns.map((col, ci) => {
                    const sticky = stickyFirst && ci === 0;
                    return (
                      <td
                        key={col.key}
                        className={cn(
                          'px-4 py-3 text-body align-middle',
                          alignClass(col.align),
                          sticky && 'sticky left-0 z-10 bg-surface',
                          col.className
                        )}
                      >
                        {col.render ? col.render(row, ri) : row[col.key]}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {pageSize > 0 && pageCount > 1 ? (
        <div className="flex items-center justify-between gap-3 mt-3">
          <p className="text-sm text-muted">
            {sorted.length} bản ghi · Trang {safePage}/{pageCount}
          </p>
          <Pagination page={safePage} pageCount={pageCount} onChange={setPage} />
        </div>
      ) : null}
    </div>
  );
}

export default DataTable;
